import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Lock, Send, MoreHorizontal, Pencil, Trash2, Repeat2, X, Radio, Video, Users, HeartHandshake } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import { ENCOURAGEMENTS, ENCOURAGEMENT_MAP } from "./badgeIcons";
import MediaAttach from "./MediaAttach";
import MediaGrid from "./MediaGrid";
import VoiceRecorder from "./VoiceRecorder";
import VoicePlayer from "./VoicePlayer";
import LiveRoom from "./LiveRoom";
import Avatar from "./Avatar";
import { toast } from "sonner";

const prettyDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return d; } };
const memberInitials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function Encouragement({ c }) {
  const e = c.kind ? ENCOURAGEMENT_MAP[c.kind] : null;
  const Icon = e?.icon;
  return (
    <div className="text-sm font-body" data-testid={`encouragement-${c.id}`}>
      <span className="text-[#A9724E] font-medium">{c.author_name}</span>
      {e && <span className="inline-flex items-center gap-1 ml-2 text-[10px] tracking-widest uppercase text-[#8a7a6a]">{Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />}{e.label}</span>}
      {c.text && <p className="text-[#2A2825] mt-0.5 whitespace-pre-wrap">{c.text}</p>}
      {c.voice_url && <VoicePlayer path={c.voice_url} testid={`enc-voice-${c.id}`} />}
      <MediaGrid media={c.media} />
    </div>
  );
}

function FeedCard({ item, currentUserId, onDeleted, onReposted }) {
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);
  const [count, setCount] = useState(item.comment_count);
  const [composer, setComposer] = useState(null); // {kind, text, voice, media}
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.text || "");
  const [text, setText] = useState(item.text);
  const [media, setMedia] = useState(item.media || []);
  const isMine = item.user_id === currentUserId;

  const loadComments = async () => {
    if (comments !== null) return;
    try { const { data } = await portalApi.get(`/portal/entries/${item.id}`); setComments(data.comments); }
    catch { setComments([]); }
  };
  const toggle = async () => { const n = !open; setOpen(n); if (n) await loadComments(); };

  const openComposer = async (enc) => { setOpen(true); await loadComments(); setComposer({ kind: enc.kind, text: enc.prompt, voice: null, media: [] }); };

  const sendEncouragement = async () => {
    if (!composer.text.trim() && !composer.voice && composer.media.length === 0) return;
    try {
      const { data } = await portalApi.post(`/portal/entries/${item.id}/comments`, { kind: composer.kind, text: composer.text, voice_url: composer.voice, media: composer.media });
      setComments((c) => [...(c || []), data]); setCount((n) => n + 1); setComposer(null);
      toast.success("Encouragement sent.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEdit = async () => {
    try { const { data } = await portalApi.put(`/portal/entries/${item.id}`, { text: editText }); setText(data.text); setEditing(false); toast.success("Updated."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    try { await portalApi.delete(`/portal/entries/${item.id}`); onDeleted(item.id); toast.success("Removed."); }
    catch (e) { toast.error(apiError(e)); }
  };
  const repost = async () => {
    try { const { data } = await portalApi.post(`/portal/entries/${item.id}/repost`); onReposted(data); setMenu(false); toast.success("Reposted."); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <article data-testid={`feed-card-${item.id}`} className="bg-white/60 border border-[#E0D8C9] rounded-xl p-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <Link to={`/portal/profile/${item.user_id}`} className="flex items-center gap-2.5 group">
          <Avatar name={item.author_name} avatar={item.author_avatar} size={32} />
          <span className="font-serif-display text-lg text-[#2A2825] group-hover:text-[#A9724E] transition-colors">{item.author_name}</span>
        </Link>
        <div className="flex items-center gap-2 relative">
          {item.is_private && <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#b45c5c]"><Lock className="w-3 h-3" strokeWidth={1.5} />private</span>}
          {isMine && (
            <button data-testid={`post-menu-${item.id}`} onClick={() => setMenu((m) => !m)} className="text-[#8a7a6a] hover:text-[#A9724E] p-1"><MoreHorizontal className="w-4 h-4" strokeWidth={1.5} /></button>
          )}
          {menu && (
            <div className="absolute right-0 top-7 z-20 w-36 bg-white rounded-lg shadow-xl border border-[#E0D8C9] overflow-hidden">
              <button data-testid={`post-edit-${item.id}`} onClick={() => { setEditing(true); setMenu(false); }} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#2A2825] hover:bg-[#EAE3D9]"><Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />Edit</button>
              <button data-testid={`post-repost-${item.id}`} onClick={repost} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#2A2825] hover:bg-[#EAE3D9]"><Repeat2 className="w-3.5 h-3.5" strokeWidth={1.5} />Repost</button>
              <button data-testid={`post-delete-${item.id}`} onClick={del} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-[#b45c5c] hover:bg-[#EAE3D9]"><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />Delete</button>
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea data-testid={`post-edit-input-${item.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] resize-none" />
          <div className="flex gap-2"><button data-testid={`post-edit-save-${item.id}`} onClick={saveEdit} className="rounded-full bg-[#A9724E] text-[#F4EFE6] px-4 py-1.5 text-sm">Save</button><button onClick={() => setEditing(false)} className="text-sm text-[#8a7a6a]">Cancel</button></div>
        </div>
      ) : (
        <p className="text-[#2A2825] font-body leading-relaxed whitespace-pre-wrap">{text}</p>
      )}
      <MediaGrid media={media} testid={`feed-media-${item.id}`} />

      <div className="flex flex-wrap items-center gap-1.5 mt-5 pt-4 border-t border-[#E0D8C9]/70">
        {ENCOURAGEMENTS.slice(0, 6).map((enc) => (
          <button key={enc.kind} data-testid={`encourage-${enc.kind}-${item.id}`} onClick={() => openComposer(enc)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#EAE3D9] text-[#5E4B3C] hover:bg-[#e0d8c9] transition-colors">
            <enc.icon className="w-3.5 h-3.5" strokeWidth={1.5} />{enc.label}
          </button>
        ))}
        <button data-testid={`comments-toggle-${item.id}`} onClick={toggle} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-[#5E4B3C] hover:bg-[#EAE3D9] transition-colors ml-auto">
          <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.5} />{count}
        </button>
      </div>

      {open && (
        <div className="mt-4 border-t border-[#E0D8C9]/70 pt-4 space-y-4">
          {(comments || []).map((c) => <Encouragement key={c.id} c={c} />)}
          {comments && comments.length === 0 && !composer && <p className="text-sm text-[#8a7a6a] font-body">Be the first to encourage — intentionally.</p>}

          {composer && (
            <div data-testid={`composer-${item.id}`} className="bg-[#F4EFE6] border border-[#E0D8C9] rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] tracking-widest uppercase text-[#A9724E]">{ENCOURAGEMENT_MAP[composer.kind]?.label}</p>
                <button onClick={() => setComposer(null)} className="text-[#8a7a6a]"><X className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
              <textarea data-testid={`composer-text-${item.id}`} value={composer.text} onChange={(e) => setComposer({ ...composer, text: e.target.value })} rows={2}
                className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 text-sm font-body outline-none focus:border-[#A9724E] resize-none" />
              {composer.voice && <div className="mt-2"><VoicePlayer path={composer.voice} /></div>}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-2">
                  <VoiceRecorder testid={`composer-voice-${item.id}`} onRecorded={(p) => setComposer((c) => ({ ...c, voice: p }))} />
                  <MediaAttach testid={`composer-media-${item.id}`} value={composer.media} onChange={(m) => setComposer((c) => ({ ...c, media: m }))} scope="cohort" />
                </div>
                <button data-testid={`composer-send-${item.id}`} onClick={sendEncouragement} className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-5 py-2 text-sm transition-colors">Send</button>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export default function Community() {
  const { user } = usePortalAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postText, setPostText] = useState("");
  const [postMedia, setPostMedia] = useState([]);
  const [posting, setPosting] = useState(false);
  const [members, setMembers] = useState([]);
  const [pulse, setPulse] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [liveRoom, setLiveRoom] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [callTitle, setCallTitle] = useState("");
  const [callScope, setCallScope] = useState("cohort");
  const [selected, setSelected] = useState([]);
  const [starting, setStarting] = useState(false);
  const [encTarget, setEncTarget] = useState(null);
  const [encMsg, setEncMsg] = useState("");
  const [encSending, setEncSending] = useState(false);

  const loadSessions = () => portalApi.get("/portal/live/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => {
    portalApi.get("/portal/feed").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
    portalApi.get("/portal/directory").then((r) => setMembers(r.data)).catch(() => {});
    portalApi.get("/portal/engagement").then((r) => setPulse(r.data)).catch(() => {});
    loadSessions();
    const id = setInterval(loadSessions, 20000);
    return () => clearInterval(id);
  }, []);

  const createPost = async () => {
    if (!postText.trim() && postMedia.length === 0) return;
    setPosting(true);
    try {
      const { data } = await portalApi.post("/portal/posts", { text: postText, media: postMedia });
      setItems((it) => [data, ...it]); setPostText(""); setPostMedia([]);
      toast.success("Shared with your cohort.");
    } catch (e) { toast.error(apiError(e)); } finally { setPosting(false); }
  };

  const startCall = async () => {
    setStarting(true);
    try {
      const body = { title: callTitle || "Live call", scope: callScope, member_ids: callScope === "private" ? selected : [] };
      const { data } = await portalApi.post("/portal/live/session", body);
      setStartOpen(false); setCallTitle(""); setSelected([]); setCallScope("cohort");
      setLiveRoom({ scope: "session", targetId: data.id, title: data.title, isHost: true, sessionId: data.id });
      loadSessions();
    } catch (e) { toast.error(apiError(e)); } finally { setStarting(false); }
  };

  const joinSession = (s) => setLiveRoom({ scope: "session", targetId: s.id, title: s.title, isHost: s.is_host, sessionId: s.id });

  const sendEncourage = async () => {
    if (!encMsg.trim()) return;
    setEncSending(true);
    try {
      await portalApi.post("/portal/encourage", { member_id: encTarget.id, message: encMsg });
      toast.success(`A gentle note is on its way to ${encTarget.name.split(" ")[0]}.`);
      setEncTarget(null); setEncMsg("");
    } catch (e) { toast.error(apiError(e)); } finally { setEncSending(false); }
  };

  const toggleSelected = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const grouped = useMemo(() => {
    const map = {};
    for (const it of items) { const d = it.date || (it.created_at || "").slice(0, 10); (map[d] = map[d] || []).push(it); }
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items]);

  const quiet = (pulse?.members || []).filter((m) => m.quiet && m.id !== user?.id);
  const lastActive = (d) => (d === null ? "not yet" : d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`);

  return (
    <div data-testid="portal-community" className="max-w-5xl">
      <div className="grid lg:grid-cols-[1fr_280px] gap-10 items-start">
        <div className="max-w-2xl min-w-0">
      <div className="flex items-start justify-between gap-4 mb-3">
        <p className="tracking-widest text-xs uppercase text-[#A9724E]">Poured out, together</p>
        <button data-testid="community-start-call" onClick={() => setStartOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-[#2A2825] hover:bg-[#413d38] text-[#F4EFE6] px-4 py-2 text-sm transition-colors">
          <Radio className="w-4 h-4" strokeWidth={1.5} />Start a call
        </button>
      </div>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-3">Community</h1>
      <p className="text-[#5E4B3C] font-body mb-6 leading-relaxed">A shared journal of your cohort's journey. No likes — only intentional encouragement: prayer, presence, and "I see you."</p>

      {sessions.length > 0 && (
        <div data-testid="live-now-strip" className="mb-8 space-y-2">
          <p className="flex items-center gap-2 text-[10px] tracking-widest uppercase text-[#A9724E]"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#A9724E] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#A9724E]" /></span>Live now</p>
          {sessions.map((s) => (
            <div key={s.id} data-testid={`live-session-${s.id}`} className="flex items-center justify-between gap-3 bg-white/70 border border-[#E0D0BC] rounded-xl p-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <Avatar name={s.host_name} avatar={s.host_avatar} size={34} />
                <span className="min-w-0"><span className="block font-serif-display text-sm text-[#2A2825] truncate">{s.title}</span><span className="text-[10px] text-[#8a7a6a] font-body">{s.host_name}{s.scope === "private" ? " · private" : ""}</span></span>
              </div>
              <button data-testid={`join-session-${s.id}`} onClick={() => joinSession(s)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-4 py-2 text-xs transition-colors shrink-0"><Video className="w-3.5 h-3.5" strokeWidth={1.5} />Join</button>
            </div>
          ))}
        </div>
      )}

      <div data-testid="post-composer" className="bg-white/60 border border-[#E0D8C9] rounded-xl p-5 mb-10">
        <textarea data-testid="post-text" value={postText} onChange={(e) => setPostText(e.target.value)} rows={2} placeholder="Share a moment, a photo, a short video…"
          className="w-full bg-white border border-[#D8CEBE] rounded-md px-4 py-3 text-[#2A2825] font-body outline-none focus:border-[#A9724E] resize-none" />
        <div className="flex items-center justify-between gap-3 mt-3">
          <MediaAttach testid="post-media" value={postMedia} onChange={setPostMedia} scope="cohort" />
          <button data-testid="post-submit" onClick={createPost} disabled={posting || (!postText.trim() && postMedia.length === 0)}
            className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm tracking-wide transition-colors disabled:opacity-50 shrink-0">{posting ? "Sharing…" : "Share"}</button>
        </div>
      </div>

      {loading ? <p className="text-[#8a7a6a] font-body">Gathering…</p> : items.length === 0 ? (
        <div className="bg-white/60 border border-[#E0D8C9] rounded-xl p-8 text-center">
          <p className="font-serif-display text-2xl text-[#2A2825] mb-2">Quiet, for now.</p>
          <p className="text-[#5E4B3C] font-body">Be the first to share something with your cohort.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {grouped.map(([date, group]) => (
            <section key={date}>
              <p data-testid={`feed-date-${date}`} className="font-serif-display text-lg text-[#8a7a6a] mb-4">{prettyDate(date)}</p>
              <div className="space-y-5">
                {group.map((it) => (
                  <FeedCard key={it.id} item={it} currentUserId={user?.id}
                    onDeleted={(id) => setItems((arr) => arr.filter((x) => x.id !== id))}
                    onReposted={(d) => setItems((arr) => [d, ...arr])} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
        </div>

        <aside className="hidden lg:block sticky top-24 space-y-8">
          {pulse && quiet.length > 0 && (
            <section data-testid="community-pulse" className="bg-[#F3E9DE] border border-[#E0D0BC] rounded-xl p-5">
              <p className="flex items-center gap-2 tracking-overline text-[10px] uppercase text-[#A9724E] mb-3"><HeartHandshake className="w-3.5 h-3.5" strokeWidth={1.5} />Gone quiet — send some love</p>
              <div className="space-y-2">
                {quiet.map((m) => (
                  <div key={m.id} data-testid={`pulse-quiet-${m.id}`} className="flex items-center justify-between gap-2">
                    <Link to={`/portal/profile/${m.id}`} className="text-sm text-[#2A2825] hover:text-[#A9724E] transition-colors truncate">{m.name}<span className="text-[10px] text-[#8a7a6a]"> · {lastActive(m.last_active_days)}</span></Link>
                    <button data-testid={`encourage-btn-${m.id}`} onClick={() => { setEncTarget(m); setEncMsg(`We've missed you, ${m.name.split(" ")[0]}. No pressure at all — just praying for you and so glad you're part of this.`); }}
                      className="text-xs text-[#A9724E] hover:text-[#925C3B] shrink-0">send love</button>
                  </div>
                ))}
              </div>
              <Link to="/portal/pulse" className="inline-block mt-4 text-xs text-[#A9724E] hover:underline">See the full pulse →</Link>
            </section>
          )}

          <section data-testid="community-members">
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a] mb-4">Your home · {members.length} member{members.length === 1 ? "" : "s"}</p>
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {members.map((m) => (
                <Link key={m.id} to={`/portal/profile/${m.id}`} data-testid={`community-member-${m.id}`}
                  className="flex items-center gap-3 rounded-xl p-2.5 hover:bg-white/70 border border-transparent hover:border-[#E0D8C9] transition-colors">
                  <Avatar name={m.name} avatar={m.avatar} size={36} testid={`community-member-avatar-${m.id}`} />
                  <span className="min-w-0"><span className="block font-serif-display text-sm text-[#2A2825] truncate">{m.name}{m.id === user?.id && <span className="text-[#8a7a6a] italic"> · you</span>}</span><span className="text-[10px] text-[#8a7a6a] font-body">{m.drops} drops</span></span>
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      {startOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setStartOpen(false)}>
          <div data-testid="start-call-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-lg p-6 md:p-8 shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div><p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Gather live</p><h3 className="font-serif-display text-2xl">Start a call</h3></div>
              <button onClick={() => setStartOpen(false)} className="text-[#8a7a6a] hover:text-[#2A2825]"><X className="w-5 h-5" /></button>
            </div>
            <label className="block text-sm text-[#5E4B3C] mb-1.5">What's it for?</label>
            <input data-testid="call-title" value={callTitle} onChange={(e) => setCallTitle(e.target.value)} placeholder="Morning prayer, Bible study…"
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <div className="flex gap-2 mb-4">
              <button data-testid="call-scope-cohort" onClick={() => setCallScope("cohort")} className={`flex-1 rounded-lg border px-3 py-2.5 text-sm transition-colors ${callScope === "cohort" ? "border-[#A9724E] bg-[#EAE3D9]" : "border-[#D8CEBE]"}`}>Whole home</button>
              <button data-testid="call-scope-private" onClick={() => setCallScope("private")} className={`flex-1 rounded-lg border px-3 py-2.5 text-sm transition-colors ${callScope === "private" ? "border-[#A9724E] bg-[#EAE3D9]" : "border-[#D8CEBE]"}`}>Private group</button>
            </div>
            {callScope === "private" && (
              <div className="mb-4">
                <p className="text-xs text-[#8a7a6a] mb-2">Invite {selected.length > 0 ? `· ${selected.length} selected` : ""}</p>
                <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
                  {members.filter((m) => m.id !== user?.id).map((m) => (
                    <button key={m.id} data-testid={`invite-${m.id}`} onClick={() => toggleSelected(m.id)}
                      className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${selected.includes(m.id) ? "border-[#A9724E] bg-[#EAE3D9]" : "border-[#D8CEBE]"}`}>
                      <Avatar name={m.name} avatar={m.avatar} size={28} />
                      <span className="text-sm text-[#2A2825] truncate">{m.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button data-testid="start-call-confirm" onClick={startCall} disabled={starting || (callScope === "private" && selected.length === 0)}
              className="w-full rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] py-3 text-sm tracking-wide transition-colors disabled:opacity-50">
              {starting ? "Starting…" : "Start the call"}
            </button>
          </div>
        </div>
      )}

      {encTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEncTarget(null)}>
          <div data-testid="encourage-modal" className="bg-[#F4EFE6] text-[#2A2825] rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="tracking-overline text-[10px] uppercase text-[#8a7a6a]">Send a gentle note</p>
            <h3 className="font-serif-display text-2xl mb-4">to {encTarget.name}</h3>
            <textarea data-testid="encourage-message" value={encMsg} onChange={(e) => setEncMsg(e.target.value)} rows={4}
              className="w-full bg-white border border-[#D8CEBE] rounded-md px-3 py-2 font-body outline-none focus:border-[#A9724E] mb-4" />
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setEncTarget(null)} className="text-sm text-[#8a7a6a]">Cancel</button>
              <button data-testid="encourage-send" onClick={sendEncourage} disabled={encSending || !encMsg.trim()}
                className="rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-2.5 text-sm transition-colors disabled:opacity-50">{encSending ? "Sending…" : "Send note"}</button>
            </div>
          </div>
        </div>
      )}

      {liveRoom && <LiveRoom {...liveRoom} onClose={() => { setLiveRoom(null); loadSessions(); }} />}
    </div>
  );
}
